import {
  Box,
  Col,
  Container,
  PhoneInput,
  Row,
  Text
} from '@qonsoll/react-design'
import { Button, Form, Input, message } from 'antd'
import {
  StyledTextWrapper,
  loadingOverlayFirstLineStyles,
  loadingOverlaySecondLineStyles
} from './NewsTipOneStepCreation.styles'
import { useEffect, useMemo, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'

import { COLLECTIONS } from '__constants__'
import { LoadingOverlay } from 'components'
import PATHS from 'pages/paths'
import PropTypes from 'prop-types'
import { STORAGE_PATHS } from '__constants__'
import { UploadMediaWide } from 'components'
import { getId } from 'services/api/firebase'
import { useActionsNewsTipAdvancedForm } from '../NewsTipAdvancedForm/hooks'
import { useNewsTip } from 'domains/NewsTip/context'
import { useNewspaperConfig } from 'contexts'
import { usePhoneNumber } from 'hooks'
import { useTranslations } from 'contexts/Translation'

const NewsTipOneStepCreation = ({ isGuestMode = false }) => {
  const { t } = useTranslations()
  const history = useHistory()
  const location = useLocation()

  const { AUTHENTICATED } = PATHS
  const { setDraftTipData } = useNewsTip()
  const { publicWizardConfiguration } = useNewspaperConfig()

  const [form] = Form.useForm()
  const { validatePhoneNumber, transformPhoneNumber } = usePhoneNumber()
  const [isCaptchaBeingProcessed, setIsCaptchaBeingProcessed] = useState(false)

  const { saveForm } = useActionsNewsTipAdvancedForm()

  const [value, setValue] = useState({
    isGuestMode: isGuestMode,
    isCreatedFromWeb: true
  })
  const [uploadLoading, setUploadLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [isSubmitQueuedAfterUpload, setIsSubmitQueuedAfterUpload] =
    useState(false)

  useEffect(() => {
    if (!value?._id)
      setValue((prev) => ({ ...prev, _id: getId(COLLECTIONS.NEWS_TIPS) }))
  }, [value?._id])

  const haveUnloadedData = useMemo(
    () => value?.media?.files?.filter((item) => !item?.url)?.length,
    [value?.media]
  )

  useEffect(() => {
    if (!isSubmitQueuedAfterUpload) return
    if (uploadLoading) return
    if (haveUnloadedData) return

    setIsSubmitQueuedAfterUpload(false)
    form.submit()
  }, [form, haveUnloadedData, isSubmitQueuedAfterUpload, uploadLoading])

  const handleMediaChange = (media) => {
    setValue((prev) => ({ ...prev, media }))
  }

  const onSubmit = async (formValues) => {
    if (isSaving) return
    if (haveUnloadedData) {
      setIsSubmitQueuedAfterUpload(true)
      const element = document.getElementById('uploadMedia')
      element?.click?.()
      return
    }

    setIsSaving(true)

    const rawPhone = formValues?.contactPhoneNumber
    const normalizedPhone =
      !rawPhone || rawPhone === '+47'
        ? null
        : validatePhoneNumber(rawPhone).phone
    const isPhoneValid =
      !rawPhone || rawPhone === '+47'
        ? true
        : validatePhoneNumber(rawPhone).valid

    const preparedTip = {
      ...value,
      content: formValues?.content ?? null,
      contactEmail: formValues?.contactEmail ?? null,
      contactPhoneNumber: isPhoneValid
        ? normalizedPhone
          ? transformPhoneNumber(normalizedPhone)
          : null
        : null
    }
    try {
      setIsCaptchaBeingProcessed(true)
      const preparedDataToSave = await new Promise((resolve, reject) => {
        saveForm(
          { newsTip: preparedTip, isGuestMode: isGuestMode },
          (_data, errorText) => {
            if (errorText) {
              return reject(new Error(errorText))
            }

            return resolve(_data)
          }
        )
      })

      if (isGuestMode) {
        setDraftTipData?.(preparedDataToSave)
        history.push(PATHS.UNAUTHENTICATED.SIGNUP)
      } else {
        history.push(AUTHENTICATED.NEWS_TIP_SUCCESSFULLY_CREATED)
      }
    } catch (e) {
      message.error(e?.message || 'Something went wrong during data save', 4)
    } finally {
      setIsCaptchaBeingProcessed(false)
      setIsSaving(false)
    }
  }

  const loadingTextSecondLineComputed = t('Thank you for your patience') + '.'
  const loadingTextFirstLineComputed =
    t('Verifying your access for security purposes') +
    '. ' +
    t('Please wait a moment while we ensure your session is secure') +
    '.'

  const showBlockingOverlay =
    uploadLoading ||
    isSubmitQueuedAfterUpload ||
    isSaving ||
    isCaptchaBeingProcessed

  const isFormBusy = uploadLoading || isSubmitQueuedAfterUpload || isSaving

  const isPublicWizard =
    location?.pathname !== PATHS.AUTHENTICATED.NEWS_TIP_WIZARD
  const computedPublicWizardText = publicWizardConfiguration?.publicWizardText

  return (
    <>
      <Container
        bounded
        display="flex"
        justifyContent="center"
        flexDirection="column"
        pb={3}>
        <Row noGutters justifyContent="center">
          <Col cw={[12, 12, 10]} center>
            <Form
              form={form}
              layout="vertical"
              onFinish={onSubmit}
              autoComplete="off">
              <Form.Item
                name="content"
                rules={[{ required: true, message: 'Please enter tip text' }]}>
                <Input.TextArea
                  autoSize={{ minRows: 5, maxRows: 15 }}
                  placeholder={`${t('Write your tip here')}....`}
                />
              </Form.Item>

              <Box>
                <Form.Item
                  name="contactPhoneNumber"
                  initialValue="+47"
                  rules={[
                    () => ({
                      validator(_, phone) {
                        if (!phone || phone === '+47') return Promise.resolve()
                        if (validatePhoneNumber(phone).valid)
                          return Promise.resolve()
                        return Promise.reject(
                          new Error(t('Invalid phone number'))
                        )
                      }
                    })
                  ]}>
                  <PhoneInput
                    country="no"
                    preferredCountries={['no']}
                    specialLabel=""
                    autoFormat
                    placeholder={t('Your phone number')}
                  />
                </Form.Item>
              </Box>

              <Box>
                <Form.Item
                  extra={
                    <Text type="secondary" italic>
                      {`*${t('In case the editors need to contact you')}.`}
                    </Text>
                  }
                  name="contactEmail"
                  rules={[
                    () => ({
                      validator(_, email) {
                        if (!email) return Promise.resolve()
                        const isValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(
                          String(email).trim()
                        )
                        if (isValid) return Promise.resolve()
                        return Promise.reject(new Error('Invalid email'))
                      }
                    })
                  ]}>
                  <Input placeholder={t('Your email')} />
                </Form.Item>
              </Box>

              <Box mb={4} width="100%">
                <UploadMediaWide
                  value={value?.media}
                  tipId={value?._id}
                  onChange={handleMediaChange}
                  maxCount={1}
                  setUploadLoading={setUploadLoading}
                  storagePath={STORAGE_PATHS.TIPS}
                  isGuestMode={isGuestMode}
                />
              </Box>

              <Button
                type="primary"
                htmlType="submit"
                id="registeredSinglePageSubmit"
                block
                size="large"
                loading={!!isFormBusy}
                disabled={!!isFormBusy}
                onClick={() => form.submit()}>
                {t('Submit')}
              </Button>
            </Form>
            {isPublicWizard ? (
              <Row noGutters>
                <Col mt="16px" cw={12}>
                  <StyledTextWrapper
                    dangerouslySetInnerHTML={{
                      __html: computedPublicWizardText
                    }}
                  />
                </Col>
              </Row>
            ) : null}
          </Col>
        </Row>
      </Container>

      {showBlockingOverlay ? (
        <LoadingOverlay
          loadingTextComponent={
            <>
              <Row mt={2} h="center">
                <Col cw={12}>
                  <Text style={loadingOverlayFirstLineStyles}>
                    {loadingTextFirstLineComputed}
                  </Text>
                </Col>
              </Row>
              <Row mt={3} h="center">
                <Col cw={12}>
                  <Text style={loadingOverlaySecondLineStyles}>
                    {loadingTextSecondLineComputed}
                  </Text>
                </Col>
              </Row>
            </>
          }
        />
      ) : null}
    </>
  )
}

NewsTipOneStepCreation.propTypes = {
  isGuestMode: PropTypes.bool
}

export default NewsTipOneStepCreation
